import { useApiResponse } from '@/composables/useApiResponse';
import { useLaunchToast } from '@/composables/useToast';
import { ONE_MINUTE_IN_MS, QUERY_USERS } from '@/constants';
import { CreditNoteProcessAutomatedStatus, type UserResponse } from '@/generated';
import { AutomaticProcessingStatus } from '@/models/automaticProcessingStatus';
import useAuthStore from '@/stores/useAuthStore';
import useSignalRStore from '@/stores/useSignalRStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { type MaybeRefOrGetter, computed, toValue, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { creditNoteApi, userApi } from './generatedApi';

const hasValidUserId = (user: UserResponse): user is UserResponse & { userId: string } =>
    !!user.userId?.trim();

export const getUserIds = (users: UserResponse[]) =>
    users.filter(hasValidUserId).map(user => user.userId);

export const getUserOptions = (users: UserResponse[]) =>
    users.filter(hasValidUserId).map(user => {
        const name = [user.firstName?.trim(), user.lastName?.trim()].filter(Boolean).join(' ');
        return {
            value: user.userId,
            label: name || user.userId,
        };
    });

export const fetchUsers = async (): Promise<UserResponse[]> => {
    const apiResponseCode = useApiResponse();
    const response = await userApi.apiV1UserGet();
    if (!apiResponseCode(response.status).ok) {
        throw new Error(`Could not load users (status: ${response.status})`);
    }
    return response.data;
};

export const getUsersQueryOptions = () => {
    const queryKey: string[] = [QUERY_USERS];
    return {
        queryKey,
        queryFn: fetchUsers,
        staleTime: ONE_MINUTE_IN_MS,
    };
};

export const useGetUsers = (enabled: MaybeRefOrGetter<boolean> = true) => {
    const { isAuthenticated, isAdmin } = storeToRefs(useAuthStore());
    const toast = useLaunchToast();
    const { t } = useI18n();

    const query = useQuery({
        ...getUsersQueryOptions(),
        enabled: computed(() => toValue(enabled) && isAuthenticated.value && isAdmin.value),
    });

    watch(query.error, error => {
        if (error) {
            toast('error', t('page.ordersProcessedAdmin.usersError'))();
        }
    });

    return query;
};

export const QUERY_AUTOMATIC_PROCESSING_PAUSED = 'automatic-processing-paused';
export const AUTOMATIC_PROCESSING_PAUSED_QUERY_KEY = [QUERY_AUTOMATIC_PROCESSING_PAUSED] as const;

export const useAutomaticProcessingPaused = () => {
    const apiResponseCode = useApiResponse();
    const { isAuthenticated, isAdmin } = storeToRefs(useAuthStore());
    const { setAutomaticProcessingStatus } = useSignalRStore();

    const query = useQuery({
        queryKey: AUTOMATIC_PROCESSING_PAUSED_QUERY_KEY,
        queryFn: async () => {
            const response = await creditNoteApi.apiV1CreditnoteProcessAutomatedStatusGet();

            if (!apiResponseCode(response.status).ok) {
                throw new Error(
                    `Could not load automatic processing status (status: ${response.status})`
                );
            }

            return response.data.status === CreditNoteProcessAutomatedStatus.Paused;
        },
        enabled: computed(() => isAuthenticated.value && isAdmin.value),
        staleTime: ONE_MINUTE_IN_MS,
    });

    watch(
        query.data,
        isPaused => {
            if (isPaused !== undefined) {
                setAutomaticProcessingStatus(
                    isPaused ? AutomaticProcessingStatus.Paused : AutomaticProcessingStatus.Active
                );
            }
        },
        { immediate: true }
    );

    return query;
};

export const useAutomaticProcessingPausedActions = () => {
    const apiResponseCode = useApiResponse();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: AUTOMATIC_PROCESSING_PAUSED_QUERY_KEY,
        mutationFn: async (isPaused: boolean): Promise<void> => {
            const status = isPaused
                ? CreditNoteProcessAutomatedStatus.Paused
                : CreditNoteProcessAutomatedStatus.Active;
            const response = await creditNoteApi.apiV1CreditnoteProcessAutomatedStatusPut({
                status,
            });

            if (!apiResponseCode(response.status).ok) {
                throw new Error(
                    `Could not set automatic processing status (status: ${response.status})`
                );
            }
        },
        onSuccess: (_, isPaused) => {
            queryClient.setQueryData<boolean>(AUTOMATIC_PROCESSING_PAUSED_QUERY_KEY, isPaused);
        },
    });
};
